// VENDORED COPY of frontend/src/lib/financial/product-pl.ts — do not edit here; keep in sync with the main app.
/**
 * Product P&L calculation.
 *
 * Computes a full P&L waterfall for a single product by segregating
 * cost items into three accounting categories and computing each line
 * independently using the existing calculateHorizon() primitive.
 *
 * P&L structure:
 *   Revenue (Value Created)   = Σ savings
 *   (–) COGS                  = Σ OPEX-COGS items
 *   = Gross Margin
 *   (–) SG&A                  = Σ OPEX-SGA items
 *   = EBIT
 *   (–) D&A                   = Σ CAPEX / useful_life_months per month
 *   = Net Contribution
 *
 * CAPEX amortization: a CAPEX cost item (one-time cash outflow) is converted
 * into a synthetic monthly-recurring HorizonItem so that calculateHorizon()
 * spreads it as D&A:  monthly D&A = cost / useful_life_months.
 */

import type { HorizonItem } from './types';
import type { HorizonOptions } from './projections';
import { calculateHorizon } from './projections';

// ── Category classification ───────────────────────────────────────────────────

/**
 * Classify a raw category string into one of three P&L buckets.
 * Uses substring matching so that admin-renamed categories
 * (e.g. 'CAPEX - Capital Expenditure') still resolve correctly.
 *
 * Exported for unit testing.
 */
export function classifyCostCategory(cat: string | null): 'CAPEX' | 'OPEX-COGS' | 'OPEX-SGA' | null {
  if (!cat) return null;
  const u = cat.toUpperCase();
  if (u.includes('CAPEX')) return 'CAPEX';
  if (u.includes('COGS') || u.includes('COST OF GOODS')) return 'OPEX-COGS';
  // Personnel rows live in SG&A by default — the master `Personnel` category
  // would otherwise fall through to `null` and be silently dropped from EBIT.
  // This affects existing Personnel cost rows across every tenant.
  if (u.includes('PERSONNEL')) return 'OPEX-SGA';
  if (u.includes('SG&A') || u.includes('SGA') || u.includes('SELLING, GENERAL')) return 'OPEX-SGA';
  return null;
}

/** Default useful life when a CAPEX item has no useful_life_months set. */
const DEFAULT_USEFUL_LIFE_MONTHS = 36;

// ── Types ────────────────────────────────────────────────────────────────────

/** Raw cost item as returned by the product costs API. */
type RawCostItem = Record<string, unknown>;

/** A single cost-driver contribution within a P&L sub-line. */
export interface PLDriverLine {
  name: string;
  total: number;
}

/** A P&L sub-line grouping within a cost category (e.g. "SG&A – Personnel"). */
export interface PLSubLine {
  label: string;
  total: number;
  drivers: PLDriverLine[];
}

/** A single P&L line with monthly array, 36-month total, and per-sub-line breakdown. */
export interface PLLineResult {
  monthly: number[];
  total: number;
  /**
   * Per-sub-line totals grouped by pl_line label.
   * Empty for derived lines (grossMargin, ebit, netContribution).
   * Items with no pl_line assigned appear under an "Other" sub-line.
   */
  plLines: PLSubLine[];
}

/** Full P&L waterfall result for a product. */
export interface PLResult {
  revenue: PLLineResult;        // Σ savings
  cogs: PLLineResult;           // Σ OPEX-COGS
  grossMargin: PLLineResult;    // revenue – cogs
  sga: PLLineResult;            // Σ OPEX-SGA
  ebit: PLLineResult;           // grossMargin – sga
  da: PLLineResult;             // Σ CAPEX D&A
  netContribution: PLLineResult; // ebit – da
}

// ── Internal helpers ─────────────────────────────────────────────────────────

function getCostCategory(item: RawCostItem): 'CAPEX' | 'OPEX-COGS' | 'OPEX-SGA' | null {
  const driver = item.cost_driver as Record<string, unknown> | null | undefined;
  return classifyCostCategory((driver?.category as string) ?? null);
}

/**
 * Convert a standard OPEX cost item (COGS or SG&A) to a HorizonItem.
 * Preserves the existing recurring / one-time / formula semantics.
 */
function opexToHorizonItem(item: RawCostItem): HorizonItem {
  return {
    type: (item.recurring as boolean) ? 'recurring' : 'one_time',
    value: (item.cost as number) || 0,
    effective_date: (item.effective_date as string) || undefined,
    formula: (item.formula as string) || undefined,
    formula_cap: (item.formula_cap as number | null) ?? null,
    interval: (item.recurring_interval as number | string) || undefined,
    recurring_terminates: (item.recurring_terminates as number) || undefined,
  };
}

/**
 * Convert a CAPEX cost item into a synthetic monthly-recurring HorizonItem
 * that represents the monthly D&A charge.
 *
 * D&A per month = cost / useful_life_months (linear / straight-line).
 * The item terminates after useful_life_months periods.
 */
function capexToDAItem(item: RawCostItem): HorizonItem {
  const usefulLife = (item.useful_life_months as number) || DEFAULT_USEFUL_LIFE_MONTHS;
  const cost = (item.cost as number) || 0;
  return {
    type: 'recurring',
    value: cost / usefulLife,        // monthly D&A amount
    interval: 1,                      // numeric 1 → monthly (via INTERVAL_MAP)
    recurring_terminates: usefulLife, // terminates after useful life
    formula: 'base',                  // flat — straight-line depreciation
    formula_cap: null,
    effective_date: (item.effective_date as string) || undefined,
  };
}

/**
 * Convert a savings item to a HorizonItem.
 * Savings items use either `.savings` or `.value` as the monetary field.
 */
function savingsToHorizonItem(item: RawCostItem): HorizonItem {
  return {
    type: (item.recurring as boolean) ? 'recurring' : 'one_time',
    value: (item.value as number) || (item.savings as number) || 0,
    effective_date: (item.savings_effective as string) || (item.effective_date as string) || undefined,
    formula: (item.formula as string) || undefined,
    formula_cap: (item.formula_cap as number | null) ?? null,
    interval: (item.recurring_interval as number | string) || undefined,
    recurring_terminates: (item.recurring_terminates as number) || undefined,
  };
}

/** Extract the cost driver name from a raw cost item. */
function getDriverName(item: RawCostItem): string {
  const driver = item.cost_driver as Record<string, unknown> | null | undefined;
  return (driver?.name as string) || 'Unknown';
}

/** Sum all values in an array. */
function sumArr(arr: number[]): number {
  return arr.reduce((a, b) => a + b, 0);
}

/** Zip two monthly arrays by subtracting b from a. */
function subtractMonthly(a: number[], b: number[]): number[] {
  return a.map((v, i) => v - (b[i] ?? 0));
}

/** Wrap a monthly array into a PLLineResult (no sub-line breakdown). */
function toLine(monthly: number[]): PLLineResult {
  return { monthly, total: sumArr(monthly), plLines: [] };
}

/**
 * Group raw cost items by their pl_line label, compute per-driver totals
 * within each group, and return an ordered list of PLSubLine entries.
 *
 * Items with no pl_line are collected under an "Other" bucket appended last.
 */
function buildPLSubLines(
  rawItems: RawCostItem[],
  horizonItems: HorizonItem[],
  opts: HorizonOptions,
): PLSubLine[] {
  // Collect driver totals and group by pl_line
  const groups = new Map<string, PLDriverLine[]>();
  const OTHER = 'Other';

  rawItems.forEach((raw, i) => {
    const plLineLabel = (raw.cost_driver as Record<string, unknown> | null | undefined)
      ?.pl_line as string | null | undefined;
    const label = plLineLabel || OTHER;
    const driverTotal = calculateHorizon([horizonItems[i]], opts).total;
    const driverName = getDriverName(raw);

    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push({ name: driverName, total: driverTotal });
  });

  // Build result: named groups first (in insertion order), Other last
  const result: PLSubLine[] = [];
  groups.forEach((drivers, label) => {
    if (label !== OTHER) {
      result.push({ label, total: drivers.reduce((s, d) => s + d.total, 0), drivers });
    }
  });
  if (groups.has(OTHER)) {
    const drivers = groups.get(OTHER)!;
    result.push({ label: OTHER, total: drivers.reduce((s, d) => s + d.total, 0), drivers });
  }

  return result;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Calculate a full P&L waterfall for a product.
 *
 * @param costsData  - Raw costs response: { one_time: [...], recurring: [...] }
 * @param savingsData - Raw savings response: { one_time: [...], recurring: [...] }
 * @param options    - Horizon options (horizonMonths defaults to 36)
 * @returns PLResult with monthly arrays and 36-month totals for each P&L line
 */
export function calculateProductPL(
  costsData: { one_time: RawCostItem[]; recurring: RawCostItem[] },
  savingsData: { one_time: RawCostItem[]; recurring: RawCostItem[] },
  options: HorizonOptions = {},
): PLResult {
  const opts: HorizonOptions = { horizonMonths: 36, ...options };

  // Flatten all cost items
  const allCosts = [...costsData.one_time, ...costsData.recurring];
  const allSavings = [...savingsData.one_time, ...savingsData.recurring];

  // Segregate costs by accounting category (keep raw items alongside HorizonItems)
  const cogsRaw    = allCosts.filter(c => getCostCategory(c) === 'OPEX-COGS');
  const sgaRaw     = allCosts.filter(c => getCostCategory(c) === 'OPEX-SGA');
  const capexRaw   = allCosts.filter(c => getCostCategory(c) === 'CAPEX');
  // Items with no recognised category are excluded from the P&L (not an error)

  const cogsItems  = cogsRaw.map(opexToHorizonItem);
  const sgaItems   = sgaRaw.map(opexToHorizonItem);
  const capexItems = capexRaw.map(capexToDAItem);

  // Calculate each horizon independently — calculateHorizon() is unchanged
  const revenue = calculateHorizon(allSavings.map(savingsToHorizonItem), opts);
  const cogs    = calculateHorizon(cogsItems, opts);
  const sga     = calculateHorizon(sgaItems, opts);
  const da      = calculateHorizon(capexItems, opts);

  // Per-sub-line breakdowns for the three cost P&L lines
  const cogsSubLines  = buildPLSubLines(cogsRaw, cogsItems, opts);
  const sgaSubLines   = buildPLSubLines(sgaRaw, sgaItems, opts);
  const daSubLines    = buildPLSubLines(capexRaw, capexItems, opts);

  // Derive intermediate P&L lines by combining monthly arrays
  const grossMarginMonthly   = subtractMonthly(revenue.monthly, cogs.monthly);
  const ebitMonthly          = subtractMonthly(grossMarginMonthly, sga.monthly);
  const netContribMonthly    = subtractMonthly(ebitMonthly, da.monthly);

  return {
    revenue:         toLine(revenue.monthly),
    cogs:            { ...toLine(cogs.monthly), plLines: cogsSubLines },
    grossMargin:     toLine(grossMarginMonthly),
    sga:             { ...toLine(sga.monthly), plLines: sgaSubLines },
    ebit:            toLine(ebitMonthly),
    da:              { ...toLine(da.monthly), plLines: daSubLines },
    netContribution: toLine(netContribMonthly),
  };
}
